import React, { useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Button, DataTable, Field, Input, PageHeader, SectionCard, Switch, TableCard } from "@/components/ui";

const INITIAL = [
  { type: "Annual Leave", days: 30, carry: true, enabled: true },
  { type: "Sick Leave", days: 90, carry: false, enabled: true, note: "15 full / 30 half / 45 unpaid" },
  { type: "Emergency Leave", days: 3, carry: false, enabled: true },
  { type: "Unpaid Leave", days: 0, carry: false, enabled: true },
  { type: "Maternity Leave", days: 60, carry: false, enabled: true, note: "45 full / 15 half pay" },
  { type: "Paternity Leave", days: 5, carry: false, enabled: true },
  { type: "Compassionate Leave", days: 5, carry: false, enabled: true },
  { type: "Study Leave", days: 10, carry: true, enabled: false },
  { type: "Half Day Leave", days: 0, carry: false, enabled: true },
];

const WORKFLOW = ["Employee submits", "Reporting head reviews", "HR admin records"];

export default function LeaveConfiguration() {
  const [policies, setPolicies] = useState(INITIAL);
  const [rules, setRules] = useState({ weekend: true, ramadan: false, halfDay: true });

  const update = (type, patch) => setPolicies((ps) => ps.map((p) => (p.type === type ? { ...p, ...patch } : p)));

  const columns = [
    { key: "type", header: "Leave type", render: (p) => (
      <div><p className="text-xs font-bold text-brand-blue">{p.type}</p>{p.note && <p className="text-[11px] text-slate-500">{p.note}</p>}</div>
    ) },
    { key: "days", header: "Days / year", render: (p) => (
      <Input type="number" min={0} value={p.days} onChange={(e) => update(p.type, { days: Number(e.target.value) })} className="w-20 py-1.5 text-center" aria-label={`${p.type} days`} />
    ) },
    { key: "carry", header: "Carry fwd", render: (p) => <Switch size="sm" checked={p.carry} onChange={(v) => update(p.type, { carry: v })} label={`${p.type} carry forward`} /> },
    { key: "enabled", header: "Enabled", render: (p) => <Switch size="sm" checked={p.enabled} onChange={(v) => update(p.type, { enabled: v })} label={`${p.type} enabled`} /> },
  ];

  return (
    <>
      <PageHeader
        description="UAE Labour Law–compliant leave policies. Changes here are a preview and aren't applied to balances."
        actions={<Button variant="primary" icon={<Save size={14} />} onClick={() => toast.success("Policy preview saved")}>Save changes</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TableCard heading={`${policies.filter((p) => p.enabled).length} of ${policies.length} leave types enabled`} className="lg:col-span-2">
          <DataTable columns={columns} rows={policies} rowKey="type" />
        </TableCard>

        <div className="space-y-4">
          <SectionCard title="Leave Cycle">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Starts"><Input value="01 Jan" disabled /></Field>
              <Field label="Ends"><Input value="31 Dec" disabled /></Field>
            </div>
          </SectionCard>

          <SectionCard title="Working Days">
            <div className="space-y-3.5">
              {[["weekend", "Sat / Sun weekly off"], ["ramadan", "Ramadan reduced hours"], ["halfDay", "Half-day leave allowed"]].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold text-slate-600">{label}</span>
                  <Switch size="sm" checked={rules[k]} onChange={(v) => setRules((r) => ({ ...r, [k]: v }))} label={label} />
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Approval Workflow">
            <ol className="space-y-2.5">
              {WORKFLOW.map((step, i) => (
                <li key={step} className="flex items-center gap-3 text-xs font-semibold text-slate-600">
                  <span className="h-7 w-7 rounded-lg shadow-neu-inset-xs flex items-center justify-center font-extrabold text-brand-orange">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </SectionCard>
        </div>
      </div>
    </>
  );
}
